import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Alert,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../contexts/AuthContext';
import { getFeaturedEvent, updateFeaturedEvent, isUserAdmin, addAdmin, checkIfAnyAdminExists } from '../../firebase/firestore';

function EditFeaturedEventForm({ open, onClose, onSuccess }) {
    const { currentUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [featuredEvent, setFeaturedEvent] = useState({
        title: '',
        date: '',
        location: '',
        prizes: '',
        description: '',
        registrationUrl: '',
        detailsUrl: '',
        backgroundImage: '',
        eventImage: '',
        isActive: true
    });

    useEffect(() => {
        if (open) {
            loadFeaturedEvent();
        }
    }, [open]);

    const loadFeaturedEvent = async () => {
        setLoading(true);
        setError(null);
        try {
            const event = await getFeaturedEvent();
            if (event) {
                setFeaturedEvent({
                    title: event.title || '',
                    date: event.date || '',
                    location: event.location || '',
                    prizes: event.prizes || '',
                    description: event.description || '',
                    registrationUrl: event.registrationUrl || '',
                    detailsUrl: event.detailsUrl || '',
                    backgroundImage: event.backgroundImage || '',
                    eventImage: event.eventImage || '',
                    isActive: event.isActive !== false
                });
            } else {
                // Jeśli nie ma featured eventu, załaduj domyślne dane
                setFeaturedEvent({
                    title: "Finał Połączonych Sezonów CF07-08",
                    date: "23-24 Sierpień 2025",
                    location: "Warszawa",
                    prizes: "ok. 25 000 zł w nagrodach",
                    description: "Zagraj w największym turnieju Disney Lorcana TCG w Polsce – 2 dni emocji, 2 formaty (Constructed + Draft), masa nagród, unikalne promki i limitowane maty! Nie potrzebujesz kwalifikacji – każdy może dołączyć. Uczestnicy serii CF07-08 i juniorzy mają zniżki i bonusy. Zarejestruj się online i powalcz o Booster Boxy, piny, karty promo i tytuł mistrza sezonu!",
                    registrationUrl: "https://www.facebook.com/events/1313350633495387/",
                    detailsUrl: "https://www.facebook.com/events/1313350633495387/",
                    backgroundImage: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoicmF2ZW5zYnVyZ2VyXC9maWxlXC9EaFlNeEh5UUtQTndBUFFZRTJ3cC5qcGcifQ:ravensburger:l3Jgm4nektrX9K6pj0_OW35V_EPCRG8zmI5O0B42RSM?width=1600&format=webp&quality=100",
                    eventImage: "https://firebasestorage.googleapis.com/v0/b/playlorcana-b6632.firebasestorage.app/o/graphics%2Fcf0708.jpg?alt=media&token=d116c558-4df7-4a77-8045-26d84d20b509",
                    isActive: true
                });
            }
        } catch (err) {
            console.error('Błąd podczas ładowania featured eventu:', err);
            if (err.code === 'permission-denied') {
                setError('Brak uprawnień do odczytu. Sprawdź czy jesteś zalogowany jako administrator.');
            } else {
                setError('Nie udało się załadować danych featured eventu: ' + err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFeaturedEvent(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSelectChange = (e) => {
        const { name, value } = e.target;
        setFeaturedEvent(prev => ({
            ...prev,
            [name]: value === 'true' ? true : value === 'false' ? false : value
        }));
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError(null);
        try {
            // Sprawdź czy użytkownik jest administratorem
            if (currentUser) {
                const adminStatus = await isUserAdmin(currentUser.uid);
                if (!adminStatus) {
                    // Sprawdź czy istnieją administratorzy w systemie
                    const anyAdminExists = await checkIfAnyAdminExists();

                    if (anyAdminExists) {
                        setError('Brak uprawnień administratora. Skontaktuj się z istniejącym administratorem.');
                        setSaving(false);
                        return;
                    } else {
                        // Jeśli nie ma administratorów, spróbuj dodać pierwszego
                        try {
                            await addAdmin(currentUser.uid, {
                                email: currentUser.email,
                                createdAt: new Date(),
                                role: 'admin'
                            });
                            console.log('Użytkownik został dodany jako pierwszy administrator');
                        } catch (adminErr) {
                            console.error('Błąd podczas dodawania administratora:', adminErr);
                            setError('Nie udało się dodać administratora. Sprawdź reguły Firebase.');
                            setSaving(false);
                            return;
                        }
                    }
                }
            }

            await updateFeaturedEvent(featuredEvent);
            onSuccess('Featured event został utworzony/zaktualizowany pomyślnie');
            onClose();
        } catch (err) {
            console.error('Błąd podczas aktualizacji featured eventu:', err);
            if (err.code === 'permission-denied') {
                setError('Brak uprawnień do zapisu. Sprawdź czy jesteś zalogowany jako administrator.');
            } else {
                setError('Nie udało się zaktualizować featured eventu: ' + err.message);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (!saving) {
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: 'rgba(17, 24, 39, 0.95)',
                    color: 'white',
                    borderRadius: '0.75rem',
                    border: '1px solid rgb(55, 65, 81)'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid rgb(55, 65, 81)',
                pb: 2
            }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: '600' }}>
                    Edytuj Featured Event
                </Typography>
                <IconButton
                    onClick={handleClose}
                    disabled={saving}
                    sx={{ color: 'rgb(156, 163, 175)' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {!loading && !error && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                {featuredEvent.title === "Finał Połączonych Sezonów CF07-08" ?
                                    "Ładowanie domyślnych danych featured eventu. Możesz je edytować i zapisać." :
                                    "Edytuj dane featured eventu wyświetlanego na stronie głównej."
                                }
                                {currentUser && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                            Zalogowany jako: {currentUser.email}
                                        </Typography>
                                    </Box>
                                )}
                            </Alert>
                        )}

                        <FormControl fullWidth>
                            <InputLabel sx={{ color: 'rgb(156, 163, 175)' }}>Status wydarzenia</InputLabel>
                            <Select
                                name="isActive"
                                value={featuredEvent.isActive}
                                onChange={handleSelectChange}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgb(75, 85, 99)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgb(156, 163, 175)',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgb(196, 181, 253)',
                                    },
                                    '& .MuiSelect-icon': {
                                        color: 'rgb(156, 163, 175)',
                                    },
                                    '& .MuiSelect-select': {
                                        color: 'white',
                                    },
                                }}
                            >
                                <MenuItem value={true}>Aktywny - wyświetlane na stronie głównej</MenuItem>
                                <MenuItem value={false}>Nieaktywny - ukryte</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            name="title"
                            label="Tytuł wydarzenia"
                            value={featuredEvent.title}
                            onChange={handleInputChange}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgb(75, 85, 99)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgb(156, 163, 175)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgb(196, 181, 253)',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: 'rgb(156, 163, 175)',
                                },
                                '& .MuiInputBase-input': {
                                    color: 'white',
                                },
                            }}
                        />

                        <TextField
                            name="date"
                            label="Data wydarzenia"
                            value={featuredEvent.date}
                            onChange={handleInputChange}
                            fullWidth
                            placeholder="np. 23-24 Sierpień 2025"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgb(75, 85, 99)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgb(156, 163, 175)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgb(196, 181, 253)',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: 'rgb(156, 163, 175)',
                                },
                                '& .MuiInputBase-input': {
                                    color: 'white',
                                },
                            }}
                        />

                        <TextField
                            name="location"
                            label="Lokalizacja"
                            value={featuredEvent.location}
                            onChange={handleInputChange}
                            fullWidth
                            placeholder="np. Warszawa"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgb(75, 85, 99)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgb(156, 163, 175)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgb(196, 181, 253)',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: 'rgb(156, 163, 175)',
                                },
                                '& .MuiInputBase-input': {
                                    color: 'white',
                                },
                            }}
                        />

                        <TextField
                            name="prizes"
                            label="Nagrody"
                            value={featuredEvent.prizes}
                            onChange={handleInputChange}
                            fullWidth
                            placeholder="np. ok. 25 000 zł w nagrodach"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgb(75, 85, 99)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgb(156, 163, 175)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgb(196, 181, 253)',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: 'rgb(156, 163, 175)',
                                },
                                '& .MuiInputBase-input': {
                                    color: 'white',
                                },
                            }}
                        />

                        <TextField
                            name="description"
                            label="Opis wydarzenia"
                            value={featuredEvent.description}
                            onChange={handleInputChange}
                            fullWidth
                            multiline
                            rows={4}
                            placeholder="Szczegółowy opis wydarzenia..."
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgb(75, 85, 99)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgb(156, 163, 175)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgb(196, 181, 253)',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: 'rgb(156, 163, 175)',
                                },
                                '& .MuiInputBase-input': {
                                    color: 'white',
                                },
                            }}
                        />

                        <TextField
                            name="registrationUrl"
                            label="URL rejestracji"
                            value={featuredEvent.registrationUrl}
                            onChange={handleInputChange}
                            fullWidth
                            placeholder="https://www.facebook.com/events/..."
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgb(75, 85, 99)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgb(156, 163, 175)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgb(196, 181, 253)',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: 'rgb(156, 163, 175)',
                                },
                                '& .MuiInputBase-input': {
                                    color: 'white',
                                },
                            }}
                        />

                        <TextField
                            name="detailsUrl"
                            label="URL szczegółów"
                            value={featuredEvent.detailsUrl}
                            onChange={handleInputChange}
                            fullWidth
                            placeholder="https://www.facebook.com/events/..."
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgb(75, 85, 99)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgb(156, 163, 175)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgb(196, 181, 253)',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: 'rgb(156, 163, 175)',
                                },
                                '& .MuiInputBase-input': {
                                    color: 'white',
                                },
                            }}
                        />

                        <TextField
                            name="backgroundImage"
                            label="URL obrazka tła"
                            value={featuredEvent.backgroundImage}
                            onChange={handleInputChange}
                            fullWidth
                            placeholder="https://cdn-assets-eu.frontify.com/..."
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgb(75, 85, 99)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgb(156, 163, 175)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgb(196, 181, 253)',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: 'rgb(156, 163, 175)',
                                },
                                '& .MuiInputBase-input': {
                                    color: 'white',
                                },
                            }}
                        />

                        <TextField
                            name="eventImage"
                            label="URL obrazka wydarzenia"
                            value={featuredEvent.eventImage}
                            onChange={handleInputChange}
                            fullWidth
                            placeholder="https://firebasestorage.googleapis.com/..."
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgb(75, 85, 99)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgb(156, 163, 175)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgb(196, 181, 253)',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: 'rgb(156, 163, 175)',
                                },
                                '& .MuiInputBase-input': {
                                    color: 'white',
                                },
                            }}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{
                borderTop: '1px solid rgb(55, 65, 81)',
                pt: 2,
                px: 3,
                pb: 3
            }}>
                <Button
                    onClick={handleClose}
                    disabled={saving}
                    sx={{
                        color: 'rgb(156, 163, 175)',
                        '&:hover': {
                            backgroundColor: 'rgba(156, 163, 175, 0.1)'
                        }
                    }}
                >
                    Anuluj
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={saving}
                    variant="contained"
                    sx={{
                        backgroundColor: 'rgb(76, 29, 149)',
                        '&:hover': {
                            backgroundColor: 'rgb(91, 33, 182)'
                        },
                        '&:disabled': {
                            backgroundColor: 'rgb(75, 85, 99)'
                        }
                    }}
                >
                    {saving ? <CircularProgress size={20} /> : 'Zapisz zmiany'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default EditFeaturedEventForm; 